import { ADVICEPREP_TRIAL_CONNECTIONS } from './pricing'

/**
 * One copy of the trial-limit wording for every screen that adds a connection.
 * The Platform grid and the AdvicePrep organisation list both gate on the same
 * allowance, and a customer who reads two different explanations of the same
 * block raises a ticket.
 */
export function advicePrepTrialCoverage(limit: number) {
  return `Your free trial covers ${limit} platform connections, and they are all in use.`
}

/**
 * The allowance as the UI knows it, for a tooltip or alert that has no server
 * figure to hand. Where a rejection carries the server's own `connectionsLimit`
 * that number is used instead — see advicePrepTrialLimitMessage.
 */
export const advicePrepTrialLimitHint = advicePrepTrialCoverage(ADVICEPREP_TRIAL_CONNECTIONS)

/** AdvisePrepConnections.ConnectionLimitReason, as the 409 body spells it. */
export const ADVICEPREP_TRIAL_LIMIT_REASON = 'trial_connection_limit'

export type AdvicePrepTrialLimitRejection = {
  connectionsLimit: number
  /** An exact instant from Stripe's trial_end, not a midnight boundary. */
  trialEnd: string | null
}

/**
 * The 409 from Platform/ui/create/save when the trial allowance is already
 * spent. Matched on `reason`, never on the status alone: other 409s on these
 * endpoints carry a plain `{ error }` instead and would be misread as this.
 *
 * The body has no message/error/title field, so httpClient's extractor cannot
 * find one and the thrown error reads "Request failed". The figures have to
 * come off `payload`, and they are a parsed-JSON claim rather than a check.
 */
export function advicePrepTrialLimitRejection(error: unknown): AdvicePrepTrialLimitRejection | null {
  // Matched on shape rather than `instanceof ApiRequestError`: the class
  // identity is per module instance, so an error crossing a bundle boundary
  // fails the check while looking identical. Status plus reason is the real
  // signal, and this keeps the HTTP client out of the import graph.
  if (typeof error !== 'object' || error === null) return null

  const { status, payload } = error as { status?: unknown; payload?: unknown }
  if (status !== 409) return null
  if (typeof payload !== 'object' || payload === null) return null

  const body = payload as Record<string, unknown>
  if (body['reason'] !== ADVICEPREP_TRIAL_LIMIT_REASON) return null

  const limit = body['connectionsLimit']
  const trialEnd = body['trialEnd']

  // Strictly a number, not Number(): that coerces `true` to 1 and `[5]` to 5,
  // so a junk value would silently become a tighter limit than the real one
  // and lock the client out a connection early.
  const usableLimit = typeof limit === 'number' && Number.isFinite(limit) && limit > 0

  return {
    connectionsLimit: usableLimit ? limit : ADVICEPREP_TRIAL_CONNECTIONS,
    trialEnd: typeof trialEnd === 'string' ? trialEnd : null,
  }
}

/**
 * Includes the time, not just the date: the cap lifts at Stripe's exact
 * trial_end, so a date alone invites "it is the 31st, why am I still blocked".
 */
export function formatAdvicePrepTrialEndAt(value: string | null) {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime())
    ? null
    : parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

export function advicePrepTrialLimitMessage(rejection: AdvicePrepTrialLimitRejection) {
  const endsAt = formatAdvicePrepTrialEndAt(rejection.trialEnd)
  const covered = advicePrepTrialCoverage(rejection.connectionsLimit)

  return endsAt
    ? `${covered} You can add more after the trial ends on ${endsAt}.`
    : `${covered} You can add more once the trial ends.`
}

/**
 * The trial-limit message names a date and wraps to about four lines, which the
 * toast's 5s default dismisses mid-read.
 */
export const ADVICEPREP_TRIAL_TOAST_MS = 12_000
